import { getThemeHtmlAttrs, getThemeStyles } from '@atlaskit/tokens';
import {
	type Theme,
	DEFAULT_THEME,
	ThemeOptions,
} from '@atlassian/jira-common-constants/src/theme-preference.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import type { RouterDataContext } from '@atlassian/react-resource-router';
import { getThemePreference } from '../../../services/index.tsx';
import { loadThemeTokens } from '../../../utils/load-theme-tokens.tsx';
import type { ThemePreferenceResource } from '../types.tsx';

// Theme override through query parameter:
// Use case: when Jira is embedded as an iframe inside another app that may have a different theme.
// So far this is used by Confluence, see confluence/platform/packages/linking-platform/link-create-jira/
// Ex. if Confluence is light and Jira dark, Jira embedded in Confluence should be light regardless of the jira preference
export const getThemeOverrideFromQueryParameters = ({ query }: RouterDataContext) => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	if (query?.themeMode && ThemeOptions.includes(query.themeMode as Theme)) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return query.themeMode as Theme;
	}
	return undefined;
};

const getResourceReturns = async (theme: Theme): Promise<ThemePreferenceResource> => {
	let typographyTheme:
		| 'typography-adg3'
		| 'typography-modernized'
		| 'typography-refreshed'
		| undefined;

	if (isVisualRefreshEnabled()) {
		typographyTheme = 'typography-modernized';
		if (fg('platform-dst-jira-web-fonts') || fg('platform-dst-jira-web-fonts-v3')) {
			typographyTheme = 'typography-refreshed';
		}
	}

	const attrs = getThemeHtmlAttrs({ colorMode: theme, typography: typographyTheme });
	const styles = await getThemeStyles({ colorMode: theme, typography: typographyTheme });

	loadThemeTokens(theme);

	return {
		theme,
		attrs,
		styles,
	};
};

// WARNING This function is flawed, see parent README.md
export default async function getData(
	context: RouterDataContext,
): Promise<ThemePreferenceResource> {
	try {
		const theme = getThemeOverrideFromQueryParameters(context) || (await getThemePreference());

		return await getResourceReturns(theme || DEFAULT_THEME);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (err: any) {
		log.safeErrorWithoutCustomerData(
			'spa.router.resources.navigation.themePreferenceResource',
			'themePreferenceResource',
			err,
		);
		return {};
	}
}
